import { ConnectionMode } from '@stores/destinationDefsList';
import { ConfigValue, PreRequisites } from '@components/common/formSchema/types';
import type { ConditionsConfig } from '@components/common/formGroup/conditionsEvaluator';
import { SelectFieldVariant } from '@ui-library/select';
import { DestinationConfig, IDestination } from '@stores/destination';
import { QueryResult } from '@hooks/useActionGuard/types';

export type CustomFormValue = string | boolean | { [key: string]: string }[];

interface ConditionalField {
  preRequisites?: PreRequisites;
  conditions?: ConditionsConfig;
}

export interface DestUIConfigV2 {
  baseTemplate: CollapsibleSection[];
  sdkTemplate: Group;
  consentSettingsTemplate?: Group;
  redirectGroups: { [redirectGroupKey: string]: RedirectGroup };
}

interface RedirectGroup {
  fields?: RedirectGroupField[];
  tabs?: Tab[];
}

export interface Tab extends ConditionalField {
  name: string;
  fields: RedirectGroupField[];
  isDefault?: boolean;
}

export type RedirectGroupField = InputField | CustomComponentField;

export interface CollapsibleSection {
  title: string;
  note: NoteType;
  hideEditIcon?: boolean;
  sections: Section[];
}

export interface Section extends ConditionalField {
  id?: string;
  title?: string;
  note?: NoteType;
  icon?: Icon;
  groups: Group[];
}

export interface Group extends ConditionalField {
  title: string;
  note: NoteType;
  icon?: Icon;
  callout?: Callout;
  fields: FormElement[];
  defaultConnectionModes?: Record<string, ConnectionMode>;
}

export enum ConnectionModes {
  CLOUD = 'cloud',
  DEVICE = 'device',
  HYBRID = 'hybrid',
}

export type Icon = 'settings' | 'sliders' | 'file' | 'magnifyingGlass' | 'rightToLine';

type InputField =
  | TextInputField
  | CheckboxField
  | TagInputField
  | TimePickerField
  | TimeRangePickerField
  | SingleSelectField
  | MultiSelectField
  | DynamicCustomFormField
  | MappingField
  | DynamicDataSelectField
  | AutoCompleteField
  | DynamicMultiSelectField
  | AccountManagementInputField;

export type Field = InputField | ConnectionModeField;

// Fields that own multiple flat top-level config keys instead of a single
// `configKey`. They write via the `patch` form of `OnChangeArg`.
type MultiKeyField = AudienceDeliveryApiBuilderField;

// Anything that may appear inside a Group's `fields`.
export type FormElement = Field | MultiKeyField | RedirectField;

export const isMultiKeyField = (f: FormElement): f is MultiKeyField =>
  f.type === 'audienceDeliveryApiBuilder';

export const isRedirectField = (f: FormElement): f is RedirectField => f.type === 'redirect';

// Write API for form fields. Single-key fields use `{ key, value }`; fields
// that own multiple keys (MultiKeyField) write via `{ patch }`.
export type OnChangeArg =
  | { key: string; value: ConfigValue }
  | { patch: Record<string, ConfigValue> };

// Validator registered by a field. Resolves true if valid, false otherwise.
export type FieldValidator = () => Promise<boolean>;

// `register` returns an unregister fn the field calls on unmount.
// The `id` identifies the field for the parent's validator map.
// For MultiKey fields it's `field.type`.
export type RegisterValidator = (id: string, validator: FieldValidator) => () => void;

export interface TextInputField extends ConditionalField {
  type: 'textInput';
  isTextArea?: boolean;
  label: string;
  configKey: string;
  key?: string;
  note?: NoteType;
  regex?: string;
  regexErrorMessage?: string;
  placeholder?: string;
  secret?: boolean;
  default?: string;
  value?: string;
}

export interface TagInputField extends ConditionalField {
  type: 'tagInput';
  label: string;
  configKey: string;
  tagKey: string;
  note?: NoteType;
  regex?: string;
  regexErrorMessage?: string;
  placeholder?: string;
  default?: TagsFieldValue;
  value?: TagsFieldValue;
}

export interface CheckboxField extends ConditionalField {
  type: 'checkbox';
  label: string;
  note?: NoteType;
  configKey: string;
  default?: boolean;
  value?: boolean;
}

type Enumerate<N extends number, Acc extends number[] = []> = Acc['length'] extends N
  ? Acc[number]
  : Enumerate<N, [...Acc, Acc['length']]>;

type IntRange<F extends number, T extends number> = Exclude<Enumerate<T>, Enumerate<F>>;

export interface TimePickerField extends ConditionalField {
  type: 'timePicker';
  label: string;
  note?: NoteType;
  configKey: string;
  options: { omitSeconds: boolean; minuteStep: IntRange<1, 59> };
  value?: string;
  default?: string;
}

export interface TimeRangePickerField extends ConditionalField {
  type: 'timeRangePicker';
  label: string;
  note?: NoteType;
  configKey: string;
  options: { omitSeconds: boolean; minuteStep: IntRange<1, 59> };
  startTimeLabel: string;
  startTimeKey: string;
  endTimeLabel: string;
  endTimeKey: string;
  value?: { [key: string]: string };
  default?: string;
}

type TagsFieldValue = { [tagKey: string]: string }[];

export interface SingleSelectField extends ConditionalField {
  type: 'singleSelect';
  label: string;
  configKey: string;
  key?: string;
  note?: NoteType;
  options: Option[];
  default?: string;
  value?: string;
  variant?: SelectFieldVariant;
}

export type Option = {
  label: string;
  value: string;
  disable?: boolean;
};

export interface MultiSelectField extends ConditionalField {
  type: 'multiSelect';
  label: string;
  configKey: string;
  note?: NoteType;
  options: { label: string; value: string }[];
  default?: string[];
  value?: string[];
}

export interface AccountManagementInputField extends ConditionalField {
  type: 'accountManagementInput';
  label: string;
  configKey: string;
  value?: string;
  default?: string;
}

// MultiKeyField
interface AudienceDeliveryApiBuilderField extends ConditionalField {
  type: 'audienceDeliveryApiBuilder';
  label?: string;
}

export interface ConnectionModeField extends ConditionalField {
  type: 'connectionMode';
  label: string;
  configKey: string;
  value: ConnectionMode;
  sourceType: string;
  sourceSupportedConnectionModes?: ConnectionMode[];
}

export interface RedirectField extends ConditionalField {
  type: 'redirect';
  redirectGroupKey: string;
  label: string;
  note?: NoteType;
  sourceType?: string;
}

export interface MappingField extends ConditionalField {
  type: 'mapping';
  label: string;
  note?: NoteType;
  columns: MappingColumn[];
  configKey: string;
  value?: { [key: string]: string }[];
  default?: { [key: string]: string }[];
  hideViewModeControl?: boolean;
  hideKeysJsonView?: boolean;
  separatorIcon: SeparatorIcon;
  addButtonLabel: string;
}

type SeparatorIcon = 'arrow' | 'colon';

export interface MappingRowField extends ConditionalField {
  type: 'mappingRow';
  columns: TextInputField[] | SingleSelectField[];
}

export interface DynamicCustomFormField extends ConditionalField {
  type: 'dynamicCustomForm';
  configKey: string;
  label: string;
  note?: NoteType;
  value: { [x: string]: CustomFormValue }[];
  default?: { [x: string]: CustomFormValue }[];
  rowFields: (
    | TextInputField
    | CheckboxField
    | MappingField
    | MappingRowField
    | SingleSelectField
    | TagInputField
    | DynamicDataSelectField
  )[];
  uniqueRowFields?: string[];
  apiName?: string;
  hideViewModeControl?: boolean;
  addButtonLabel: string;
}

export type MappingColumn =
  | TextInputField
  | SingleSelectField
  | DynamicDataSelectField
  | AutoCompleteField;

export type Callout = { message: string; type: 'warning' | 'info' };

export interface DynamicDataSelectField extends ConditionalField {
  placeholder?: string;
  secret?: boolean;
  type: 'dynamicDataSelect' | 'nestedSelect';
  label?: string;
  configKey: string;
  key?: string;
  note?: NoteType;
  options?: { label: string; value: string }[];
  default?: string;
  value?: string;
  apiDependencies?: string[];
  apiName?: string;
  required?: boolean;
  regex?: string;
}

export interface AutoCompleteField extends ConditionalField {
  placeholder?: string;
  type: 'autoComplete';
  label?: string;
  configKey: string;
  key?: string;
  note?: NoteType;
  options?: { label: string; value: string }[];
  default?: string;
  value?: string;
  apiName?: string;
  required?: boolean;
}

export type ConditionalNoteType = { note: BaseNote; condition: PreRequisites }[];
type BaseNote = string | (string | Hyperlink)[];
export type NoteType = BaseNote | ConditionalNoteType;

interface Hyperlink {
  text: string;
  link: string;
}

export type ExtraFormOptions = {
  isRedirectEnabled: boolean;
  componentPath: string;
  destination?: IDestination;
  sshKeyId?: string;
  destinationDefinitionName?: string;
  redirectPermission?: QueryResult;
};

export interface DynamicMultiSelectField extends ConditionalField {
  type: 'dynamicMultiSelect';
  label: string;
  configKey: string;
  note?: NoteType;
  options: { label: string; value: string }[];
  default?: string;
  value?: string;
  apiName?: string;
  mode: 'multiple' | 'tags';
}

interface CustomComponentField extends ConditionalField {
  type: 'customComponent';
  componentId: string;
  label?: string;
  note?: NoteType;
  configKey?: string;
  default?: string;
  value?: string;
  customOptions?: { [key: string]: string };
}

export interface CustomComponentProps {
  field: CustomComponentField;
  onChange: (value: string) => void;
  config: DestinationConfig;
}
